import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook for managing cleanup functions to prevent memory leaks
 * Automatically calls all registered cleanup functions on unmount
 */
export function useCleanup() {
  const cleanupFns = useRef<Array<() => void>>([]);
  
  const registerCleanup = useCallback((fn: () => void) => {
    cleanupFns.current.push(fn);
  }, []);
  
  const runCleanup = useCallback(() => {
    cleanupFns.current.forEach(fn => {
      try {
        fn();
      } catch (error) {
        console.warn('Cleanup function failed:', error);
      }
    });
    cleanupFns.current = [];
  }, []);
  
  useEffect(() => {
    return () => {
      runCleanup();
    };
  }, [runCleanup]);
  
  return { registerCleanup, runCleanup };
}

/**
 * Hook for managing timeout cleanup
 */
export function useTimeoutCleanup() {
  const timeouts = useRef<Set<NodeJS.Timeout>>(new Set());
  
  const setTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = globalThis.setTimeout(() => {
      timeouts.current.delete(timeoutId);
      callback();
    }, delay);
    
    timeouts.current.add(timeoutId);
    return timeoutId;
  }, []);
  
  const clearTimeout = useCallback((timeoutId: NodeJS.Timeout) => {
    globalThis.clearTimeout(timeoutId);
    timeouts.current.delete(timeoutId);
  }, []);
  
  const clearAllTimeouts = useCallback(() => {
    timeouts.current.forEach(id => globalThis.clearTimeout(id));
    timeouts.current.clear();
  }, []);
  
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);
  
  return { setTimeout, clearTimeout, clearAllTimeouts };
}

/**
 * Hook for managing interval cleanup
 */
export function useIntervalCleanup() {
  const intervals = useRef<Set<NodeJS.Timer>>(new Set());
  
  const setInterval = useCallback((callback: () => void, delay: number) => {
    const intervalId = globalThis.setInterval(callback, delay);
    intervals.current.add(intervalId);
    return intervalId;
  }, []);
  
  const clearInterval = useCallback((intervalId: NodeJS.Timer) => {
    globalThis.clearInterval(intervalId);
    intervals.current.delete(intervalId);
  }, []);
  
  const clearAllIntervals = useCallback(() => {
    intervals.current.forEach(id => globalThis.clearInterval(id));
    intervals.current.clear();
  }, []);
  
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, [clearAllIntervals]);
  
  return { setInterval, clearInterval, clearAllIntervals };
}